import { useState, KeyboardEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { useCustomerData } from '@/features/_data/useCustomerData';
import { useUserInfo } from '@/features/_data/useUserInfo';
import { IconEdit, IconTrash } from '@/icons';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import { Modal } from '@/ui/Modal/Modal';
import { useSelectedCustomerId } from '@/ui/Navbar/Navbar';

import { T } from '../../../../ui-components/Text/Text';

const StyledWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: fit-content;

  svg {
    cursor: pointer;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  margin-bottom: 25px;
  margin-top: 25px;
  border: 2px solid #b2b1ba;
  border-radius: 4px;
  padding: 10px;
`;

const StyledSaveButton = styled(Button)`
  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.color('#B2B1BA')};
      pointer-events: none;
    `}

  width: 100px;
  align-self: flex-end;
`;

const StyledDeleteButton = styled.button`
  padding: 10px 20px;
  background-color: #dd6c6c;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
`;

const StyledNoButton = styled.button`
  padding: 10px 20px;
  color: #000; /* Set the text color to your preference */
  background: none;
  font-weight: bold;
  border: none;
  border-radius: 0;
  cursor: pointer;
  text-decoration: underline;
  display: inline-block;
`;

const StyledFooter = styled(Flex)`
  padding: 18px 32px 32px;
`;

const StyledLabel = styled(T)`
  line-height: 21px;
`;

type Props = {
  readonly onEdit: (name: string) => void;
  readonly onDelete: () => void;
  readonly item: string;
  readonly columnName: string;
  readonly shelf?: string;
  readonly subsidiary?: string;
};

export const TableCell = ({
  item,
  columnName,
  onEdit,
  onDelete,
  shelf,
  subsidiary,
}: Props) => {
  const { isAdmin } = useUserInfo();
  const { data: customerData } = useCustomerData(isAdmin);
  const [selectedCustomerId] = useSelectedCustomerId();

  const [inputValue, setInputValue] = useState(item);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const columnItem = item;

  const currentCustomerName =
    customerData?.find(({ id }) => id === selectedCustomerId)?.name ??
    'Customer';

  const saveValueAndReset = () => {
    if (inputValue?.trim().length === 0) {
      setIsModalOpen(false);
      return;
    }

    onEdit(inputValue);
    setIsModalOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement | null>) => {
    if (event.key === 'Enter') {
      saveValueAndReset();
    }
  };

  return (
    <>
      {isModalOpen ? (
        <Modal
          footer={
            <StyledFooter grow justify="flex-end">
              <StyledSaveButton
                disabled={!inputValue.trim()}
                onClick={() => {
                  saveValueAndReset();
                }}
              >
                <FormattedMessage defaultMessage="Save" id="jvo0vs" />
              </StyledSaveButton>
            </StyledFooter>
          }
          onClose={() => setIsModalOpen(false)}
          title={
            // eslint-disable-next-line no-nested-ternary
            columnName === 'Subsidiary'
              ? `Edit ${columnName}`
              : columnName === 'Player'
                ? `${currentCustomerName} / ${subsidiary ?? 'Subsidiary'} / ${
                    shelf ?? 'Shelf'
                  }`
                : `Edit ${columnName}: ${columnItem}`
          }
        >
          <StyledInput
            autoFocus
            onChange={(event) => setInputValue(event.target.value)}
            onKeyDown={handleKeyDown}
            type="text"
            value={inputValue}
          />
        </Modal>
      ) : null}

      {isDeleteModalOpen ? (
        <Modal
          footer={
            <StyledFooter gap={12} grow justify="flex-end">
              <StyledNoButton onClick={() => setIsDeleteModalOpen(false)}>
                <FormattedMessage defaultMessage="No" id="oUWADl" />
              </StyledNoButton>
              <StyledDeleteButton onClick={() => onDelete()}>
                <FormattedMessage defaultMessage="Yes, Delete" id="QEmYhz" />
              </StyledDeleteButton>
            </StyledFooter>
          }
          onClose={() => setIsDeleteModalOpen(false)}
          title={`Deleting: ${columnItem}`}
        >
          <FormattedMessage
            defaultMessage="Are you sure you want to delete?"
            id="y7o4Rn"
          />
        </Modal>
      ) : null}

      <StyledWrapper>
        <StyledLabel body1>{item}</StyledLabel>
        <span
          data-testid="edit-table-cell-edit-icon"
          onClick={() => {
            setInputValue(item);
            setIsModalOpen(true);
          }}
        >
          <IconEdit />
        </span>
        <span
          data-testid="edit-table-cell-trash-icon"
          onClick={() => {
            setIsDeleteModalOpen(true);
          }}
        >
          <IconTrash />
        </span>
      </StyledWrapper>
    </>
  );
};
